import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useLocationContext } from "../../contexts/locationContext";
import { handleGetPublicPitches } from "../../shared/controllers/pitchController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import { useUserContext } from "../../userDashboard/contexts/userContext";
import LoadingGrid from "../../utils/loadingGrid";
import { toIso3 } from "../../utils/locationStorage";

import pin from "./assets/MapPin.svg";

interface Pitch {
    name: string;
    location: string;
    media?: {
        url: string;
    }[];
}

const PitchLanding: React.FC = () => {
    const [pitches, setPitches] = useState<Pitch[]>([]);
    const [loading, setLoading] = useState(true);
    const isAuthenticated = !!localStorage.getItem("saca_token");
    const { location: storedLocation } = useLocationContext();
    const userContext = useUserContext();
    const userProfile = userContext?.userProfile || null;

    const profileCountry = (userProfile as Record<string, unknown>)?.country_code as
        | string
        | undefined ||
        (userProfile as Record<string, unknown>)?.country as string | undefined;
    const locationCountry = storedLocation.countryCode;
    const locationState = storedLocation.state;
    const locationCity = storedLocation.city;

    const effectiveCountryCode = useMemo(() => {
        const raw = profileCountry || locationCountry;
        return toIso3(raw) || "";
    }, [profileCountry, locationCountry]);

    const fetchPitches = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, string> = {};
            if (effectiveCountryCode) {
                params.country_code = effectiveCountryCode;
            }
            if (locationState) {
                params.state = locationState;
            }
            if (locationCity) {
                params.city = locationCity;
            }

            const data = await handleGetPublicPitches(params);
            const list = unwrapApiArray<Pitch>(data);
            setPitches(list);
        } catch (error) {
            console.error("Error fetching pitches:", error);
            setPitches([]);
        } finally {
            setLoading(false);
        }
    }, [effectiveCountryCode, locationState, locationCity]);

    useEffect(() => {
        fetchPitches();
    }, [fetchPitches]);

    const displayedPitches = pitches.slice(0, 3);

    const gridColsClass =
        displayedPitches.length === 1
            ? "grid-cols-1"
            : displayedPitches.length === 2
                ? "grid-cols-2"
                : "lg:grid-cols-3";

    return (
        <div
            id="pitch-section"
            className="flex flex-col items-start justify-center py-8 md:py-16 w-full px-4 md:px-14"
        >
            <h3>Our Pitches</h3>
            <h2 className="mt-3 font-Manrope">Book a Pitch</h2>

            <div className="w-full h-auto mt-8 ">
                {loading ? (
                    <LoadingGrid count={3} />
                ) : pitches.length === 0 ? (
                    <div className="flex w-full flex-row items-center justify-center md:mt-12">
                        <h3 className="text-center font-Manrope text-lg text-black font-semibold">
                            There are no pitches available
                        </h3>
                    </div>
                ) : (
                    <div
                        className={`w-full mt-8 grid grid-cols-1 gap-8 ${gridColsClass}`}
                    >
                        {displayedPitches.map((item, index) => (
                            <div
                                key={index}
                                className="border border-[#EAEBF0] rounded-[12px] w-full"
                            >
                                <div
                                    style={{
                                        backgroundImage: item?.media?.[0]?.url
                                            ? `url(${item.media?.[0]?.url})`
                                            : undefined,
                                        backgroundPosition: "center",
                                        backgroundSize: "cover",
                                    }}
                                    className="w-full h-[250px] md:h-[350px] rounded-[12px] bg-[#B1BFC2]"
                                ></div>
                                <div className="w-full p-5 md:p-8">
                                    <p className="font-Manrope font-bold text-xl md:text-2xl text-black">
                                        {item.name}
                                    </p>
                                    <span className="flex items-center space-x-3 mt-3">
                                        <img src={pin} alt="" />
                                        <p className="font-Manrope text-sm md:text-base font-normal text-black">
                                            {item.location}
                                        </p>
                                    </span>

                                    <Link
                                        to={isAuthenticated ? "/dashboard?page=Pitch" : "/sign-in"}
                                        className="py-3 w-full bg-[#317333] block cursor-pointer rounded-md text-center mt-6 font-Manrope text-sm md:text-base font-medium text-white"
                                    >
                                        Book Pitch
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {pitches.length > 3 && (
                <Link
                    to="/pitches"
                    className="mt-8 md:mt-16 py-3 px-[18px] bg-[#317333] rounded-[6px] font-Manrope text-[#fff] font-semibold text-[15px] mx-auto"
                >
                    View more
                </Link>
            )}
        </div>
    );
};

export default PitchLanding;
